import { Link, type Href, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { Platform, Pressable, View, useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Text } from '@/components/ui/Text'
import { useI18n } from '@/contexts/I18nContext'
import { DATA_KEY } from '@/contexts/OnboardingContext'
import { useTheme, useThemeColors } from '@/contexts/ThemeContext'
import { getFirstIncompleteOnboardingStep, parseOnboardingData } from '@/lib/onboarding-step'
import { storage } from '@/lib/storage'
import TurbinaLogo from './TurbinaLogo'

type Props = {
  activeRoute?: string
  scrollOffset?: number
  transparent?: boolean
}

type NavItem = {
  label: string
  route: Href
}

// eslint-disable-next-line complexity
export default function PublicHeader({
  activeRoute = '/',
  scrollOffset = 0,
  transparent = false,
}: Props) {
  const router = useRouter()
  const { t } = useTranslation()
  const { locale, changeLanguage } = useI18n()
  const colors = useThemeColors()
  const { isDark } = useTheme()
  const insets = useSafeAreaInsets()
  const { width } = useWindowDimensions()
  const isDesktop = width >= 860
  const isCollapsed = scrollOffset > 60
  const selectedLanguage = locale === 'en' ? 'en' : 'es'

  const navItems: NavItem[] = [
    { label: t('publicHeader.whatWeDo'), route: '/about' },
    { label: t('publicHeader.help'), route: '/faq' },
    { label: t('publicHeader.login'), route: '/auth' },
  ]

  const backgroundColor = transparent ? 'transparent' : colors.background
  const navTextColor = isDark ? colors.dark : colors.dark
  const boxShadowColor = isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.08)'

  return (
    <View
      className="z-50 px-5 pb-4"
      style={{
        paddingTop: insets.top,
        borderBottomWidth: isCollapsed || !transparent ? 1 : 0,
        borderBottomColor: colors.border,
        backgroundColor,
      }}
    >
      <View className="w-full flex-row items-center justify-between">
        <Link href="/" asChild>
          <Pressable className="py-2" accessibilityRole="link">
            <TurbinaLogo width={isDesktop ? 70 : 60} />
          </Pressable>
        </Link>

        <View className="flex-row items-center gap-2">
          {isDesktop &&
            navItems.map((item) => {
              const isActive = activeRoute === item.route
              return (
                <Link key={String(item.route)} href={item.route} asChild>
                  <Pressable
                    className="px-3 py-2 rounded-full"
                    style={{ backgroundColor: isActive ? `${colors.primary}22` : 'transparent' }}
                  >
                    <Text
                      className="text-xs font-semibold uppercase tracking-[1.4px]"
                      style={{ color: isActive ? colors.primary : navTextColor }}
                    >
                      {item.label}
                    </Text>
                  </Pressable>
                </Link>
              )
            })}

          <Pressable
            testID="public-header-signup-button"
            onPress={async () => {
              const raw = await storage.get(DATA_KEY)
              const data = parseOnboardingData(raw)
              const nextStep = getFirstIncompleteOnboardingStep(data)
              if (nextStep) {
                router.push(nextStep as never)
              } else {
                router.push('/auth?mode=signup')
              }
            }}
            className="px-4 py-2 rounded-full"
            style={{ backgroundColor: colors.primary }}
          >
            <Text
              className="text-xs font-medium uppercase tracking-[1.3px]"
              style={{ color: colors.white }}
            >
              {t('publicHeader.signUp')}
            </Text>
          </Pressable>

          <Pressable
            testID="public-header-language-toggle"
            accessibilityRole="button"
            accessibilityLabel={t('settings.language')}
            onPress={() => {
              const nextLanguage = selectedLanguage === 'es' ? 'en' : 'es'
              void changeLanguage(nextLanguage)
            }}
            className="px-3 py-2 rounded-full border"
            style={{
              backgroundColor: selectedLanguage === 'es' ? `${colors.primary}14` : 'transparent',
              borderColor: selectedLanguage === 'es' ? colors.primary : colors.border,
            }}
          >
            <Text
              className="text-xs font-semibold uppercase tracking-[1.3px]"
              style={{ color: selectedLanguage === 'es' ? colors.primary : navTextColor }}
            >
              {selectedLanguage.toUpperCase()}
            </Text>
          </Pressable>
        </View>
      </View>

      {!isDesktop && (
        <View className="w-full max-w-[1200px] self-center flex-row items-center justify-center gap-2 pt-2">
          {navItems.map((item) => {
            const isActive = activeRoute === item.route
            return (
              <Link key={String(item.route)} href={item.route} asChild>
                <Pressable
                  className="px-3 py-1.5 rounded-full"
                  style={{ backgroundColor: isActive ? `${colors.primary}22` : 'transparent' }}
                >
                  <Text
                    className="text-[11px] font-semibold uppercase tracking-[1.2px]"
                    style={{ color: isActive ? colors.primary : navTextColor }}
                  >
                    {item.label}
                  </Text>
                </Pressable>
              </Link>
            )
          })}
        </View>
      )}

      {Platform.OS === 'web' && (
        <View
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            boxShadow: isCollapsed || !transparent ? `0 6px 18px ${boxShadowColor}` : 'none',
          }}
        />
      )}
    </View>
  )
}
